/* Solaine — motion and interactions.
   Durations 200–700 ms, opacity/translate only. Reveals play once. Respects prefers-reduced-motion. */
(function () {
  'use strict';

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const designMode = !!(window.Shopify && window.Shopify.designMode);
  const mobileMq = window.matchMedia('(max-width: 760px)');
  const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
  const strings = window.solaineStrings || {};

  const fmt = (s, size) => (s || '').replace('[size]', size);
  const money = (cents) => {
    const el = document.querySelector('[data-money-format]');
    const format = el ? el.dataset.moneyFormat : '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    return format
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100))
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100))
      .replace('{{amount_with_space_separator}}', amount)
      .replace('{{amount_no_decimals_with_space_separator}}', Math.round(cents / 100));
  };

  /* ---------------------------------------------------------------- Reveal */
  const reveal = {
    io: null,
    init(root) {
      if (reduced || designMode || document.documentElement.classList.contains('no-motion')) {
        document.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-revealed'));
        return;
      }
      if (!this.io) {
        this.io = new IntersectionObserver(
          (entries) =>
            entries.forEach((e) => {
              if (!e.isIntersecting) return;
              this.io.unobserve(e.target);
              this.play(e.target);
            }),
          { threshold: 0.12, rootMargin: '0px 0px -5% 0px' }
        );
      }
      (root || document).querySelectorAll('[data-reveal]').forEach((el) => {
        if (el.dataset.revealBound) return;
        el.dataset.revealBound = '1';
        if (el.dataset.reveal === 'group') {
          Array.from(el.children).forEach((child, i) => {
            child.classList.add('reveal-item');
            child.style.transitionDelay = `${i * 80}ms`;
          });
        }
        this.io.observe(el);
      });
    },
    play(el) {
      el.classList.add('is-revealed');
      window.setTimeout(() => {
        Array.from(el.children).forEach((c) => {
          c.style.transitionDelay = '';
        });
      }, 700 + el.children.length * 80);
    },
  };

  /* -------------------------------------------------------------- Parallax */
  function initParallax() {
    if (reduced || mobileMq.matches) return;
    const els = document.querySelectorAll('[data-parallax]');
    if (!els.length) return;
    let raf = 0;
    const tick = () => {
      raf = 0;
      els.forEach((el) => {
        const r = el.parentElement.getBoundingClientRect();
        if (r.bottom < 0 || r.top > window.innerHeight) return;
        const off = r.top + r.height / 2 - window.innerHeight / 2;
        el.style.transform = `translateY(${(-0.15 * off).toFixed(1)}px)`;
      });
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!raf) raf = requestAnimationFrame(tick);
      },
      { passive: true }
    );
    tick();
  }

  /* ------------------------------------------------------ Page transitions */
  function initTransitions() {
    const ov = document.querySelector('[data-page-overlay]');
    if (!ov) return;
    if (reduced || designMode || document.documentElement.classList.contains('no-transitions')) return;
    ov.classList.add('is-on');
    requestAnimationFrame(() => requestAnimationFrame(() => ov.classList.remove('is-on')));

    document.addEventListener('click', (e) => {
      const a = e.target.closest && e.target.closest('a[href]');
      if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      if (a.target === '_blank' || a.hasAttribute('download') || a.dataset.noTransition !== undefined) return;
      const href = a.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('javascript:')) return;
      let url;
      try {
        url = new URL(a.href, window.location.href);
      } catch (err) {
        return;
      }
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return;
      if (url.pathname.startsWith('/checkout') || url.pathname.startsWith('/account')) return;
      e.preventDefault();
      ov.classList.add('is-on');
      window.setTimeout(() => {
        window.location.href = url.href;
      }, 300);
    });

    window.addEventListener('pageshow', (e) => {
      if (e.persisted) ov.classList.remove('is-on');
    });
  }

  /* ---------------------------------------------------------------- Header */
  function initHeader() {
    const header = document.querySelector('[data-header]');
    if (!header) return;
    const mega = header.querySelector('[data-mega]');
    const megaTrigger = header.querySelector('[data-mega-trigger]');
    const sheet = header.querySelector('[data-menu-sheet]');
    const overHero = !!document.querySelector('.site__main > .shopify-section:first-child .hero');
    header.dataset.overHero = overHero ? 'true' : 'false';

    let scrolled = false;
    let megaOpen = false;
    let menuOpen = false;
    const apply = () => {
      const clear = overHero && !scrolled && !megaOpen && !menuOpen;
      header.classList.toggle('is-clear', clear);
      if (mega) mega.classList.toggle('is-open', megaOpen && !mobileMq.matches);
      if (megaTrigger) megaTrigger.setAttribute('aria-expanded', megaOpen ? 'true' : 'false');
    };
    const onScroll = () => {
      const s = window.scrollY > 40;
      if (s !== scrolled || megaOpen) {
        scrolled = s;
        megaOpen = false;
        apply();
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    if (mega && megaTrigger) {
      let closeT = 0;
      const open = () => {
        window.clearTimeout(closeT);
        megaOpen = true;
        apply();
      };
      const close = () => {
        closeT = window.setTimeout(() => {
          megaOpen = false;
          apply();
        }, 120);
      };
      megaTrigger.addEventListener('mouseenter', open);
      megaTrigger.addEventListener('focus', open);
      megaTrigger.addEventListener('click', (e) => {
        if (!megaOpen && !mobileMq.matches) {
          e.preventDefault();
          open();
        }
      });
      header.addEventListener('mouseleave', close);
      mega.addEventListener('mouseenter', () => window.clearTimeout(closeT));
      mega.addEventListener('mouseleave', close);
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && megaOpen) {
          megaOpen = false;
          apply();
          megaTrigger.focus();
        }
      });
      document.addEventListener('focusin', (e) => {
        if (megaOpen && !header.contains(e.target)) {
          megaOpen = false;
          apply();
        }
      });
    }

    if (sheet) {
      const toggles = header.querySelectorAll('[data-menu-toggle]');
      const setMenu = (on) => {
        menuOpen = on;
        sheet.classList.toggle('is-open', on);
        sheet.setAttribute('aria-hidden', on ? 'false' : 'true');
        document.body.classList.toggle('overflow-hidden', on);
        toggles.forEach((t) => t.setAttribute('aria-expanded', on ? 'true' : 'false'));
        apply();
        if (on && window.trapFocus) window.trapFocus(sheet, sheet.querySelector('a, button'));
        if (!on && window.removeTrapFocus) window.removeTrapFocus(toggles[0]);
      };
      toggles.forEach((t) => t.addEventListener('click', () => setMenu(!menuOpen)));
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && menuOpen) setMenu(false);
      });
      mobileMq.addEventListener('change', () => {
        if (!mobileMq.matches && menuOpen) setMenu(false);
      });
    }

    // Cart count in header updates whenever the drawer re-renders the bubble section.
  }

  /* ------------------------------------------------------------------ Hero */
  function initHero() {
    document.querySelectorAll('[data-hero]').forEach((hero) => {
      const media = hero.querySelector('.hero__media');
      const words = hero.querySelectorAll('.hero__word');
      const button = hero.querySelector('.hero__button');
      const key = 'solaine_hero_played';
      let played = false;
      try {
        played = !!sessionStorage.getItem(key);
      } catch (e) {
        played = false;
      }
      if (!reduced && !designMode && !played) {
        hero.classList.add('is-playing');
        words.forEach((w, i) => {
          w.style.animationDelay = `${i * 80}ms`;
        });
        if (button) button.style.animationDelay = `${(words.length - 1) * 80 + 900}ms`;
        try {
          sessionStorage.setItem(key, '1');
        } catch (e) {
          /* private mode */
        }
      }
      if (media && !reduced) media.classList.add('is-zooming');
    });
  }

  /* ---------------------------------------------------------- Reveal footer */
  function initFooter() {
    const footer = document.querySelector('[data-reveal-footer]');
    const spacer = document.querySelector('[data-reveal-spacer]');
    if (!footer || !spacer) return;
    const wm = footer.querySelector('.footer__wordmark');
    if (reduced) {
      footer.classList.add('footer--static');
      spacer.classList.add('reveal-spacer--off');
      return;
    }
    let raf = 0;
    let last = -1;
    const tick = () => {
      raf = 0;
      const fh = footer.getBoundingClientRect().height || window.innerHeight;
      const p = Math.max(0, Math.min(1, (window.innerHeight - spacer.getBoundingClientRect().top) / fh));
      const t = Math.max(0, Math.min(1, (p - 0.6) / 0.4));
      if (Math.abs(t - last) < 0.004) return;
      last = t;
      if (wm) {
        wm.style.setProperty('--wm-op', t.toFixed(3));
        wm.style.setProperty('--wm-tf', `translateY(${(20 * (1 - t)).toFixed(1)}px)`);
      }
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(tick);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    tick();
  }

  /* --------------------------------------------------------------- Notify */
  function bindNotify(box, size) {
    if (!box) return;
    const line = box.querySelector('[data-notify-line]');
    const form = box.querySelector('[data-notify-form]');
    const tags = box.querySelector('[data-notify-tags]');
    const input = box.querySelector('input[type="email"]');
    box.hidden = false;
    box.classList.remove('is-done');
    if (line) line.textContent = fmt(strings.notifyLine, size);
    if (input) input.placeholder = box.classList.contains('notify--compact') ? fmt(strings.notifyPlaceholder, size) : 'Your email';
    if (tags) {
      const base = tags.value.split(',').slice(0, 2).join(',');
      tags.value = size ? `${base},${size}` : base;
    }
    if (form && !form.dataset.bound) {
      form.dataset.bound = '1';
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const email = input && input.value.trim();
        if (!email) return;
        const btn = form.querySelector('[type="submit"]');
        if (btn) btn.setAttribute('aria-disabled', 'true');
        const data = new FormData(form);
        fetch(form.getAttribute('action') || '/contact', { method: 'POST', body: data, credentials: 'same-origin' })
          .catch(() => null)
          .finally(() => {
            if (btn) btn.removeAttribute('aria-disabled');
            box.classList.add('is-done');
            const chosen = (tags && tags.value.split(',')[2]) || size;
            if (line) line.textContent = fmt(strings.notifyDone, chosen);
          });
      });
    }
    if (input && !box.classList.contains('notify--compact')) window.setTimeout(() => input.focus(), 50);
  }

  /* ---------------------------------------------------------- Product card */
  function initCards(root) {
    (root || document).querySelectorAll('[data-card]').forEach((card) => {
      if (card.dataset.bound) return;
      card.dataset.bound = '1';
      let variants = [];
      try {
        variants = JSON.parse(card.querySelector('[data-card-variants]').textContent);
      } catch (e) {
        variants = [];
      }
      const sizeName = (card.dataset.sizeOption || 'Size').toLowerCase();
      const form = card.querySelector('product-form form');
      const idInput = form && form.querySelector('input[name="id"]');
      const strip = card.querySelector('.card__strip');
      const stripLabel = card.querySelector('.card__strip-label');
      const stripSizes = card.querySelector('.card__strip .card__sizes');
      const stripNotify = card.querySelector('.card__strip [data-notify]');
      const sheet = card.querySelector('[data-sheet]');
      const sheetNotify = sheet && sheet.querySelector('[data-notify]');
      const sheetSizes = sheet && sheet.querySelector('.sheet__sizes');
      const sheetNote = sheet && sheet.querySelector('.sheet__note');
      let holdT = 0;

      const variantFor = (size) => {
        const bySize = variants.filter((v) => (v.size || '').toLowerCase() === (size || '').toLowerCase());
        return bySize.find((v) => v.available) || bySize[0] || null;
      };

      const add = (variant) => {
        if (!form || !idInput || !variant) return;
        idInput.value = variant.id;
        idInput.disabled = false;
        if (stripLabel) stripLabel.textContent = strings.added || 'Added';
        card.classList.add('is-strip-on');
        window.clearTimeout(holdT);
        holdT = window.setTimeout(() => {
          if (stripLabel) stripLabel.textContent = strings.addToBag || 'Add to bag';
          card.classList.remove('is-strip-on');
        }, 2000);
        if (form.requestSubmit) form.requestSubmit();
        else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        closeSheet();
      };

      const resetStrip = () => {
        if (stripNotify) {
          stripNotify.hidden = true;
          stripNotify.classList.remove('is-done');
        }
        if (stripSizes) stripSizes.hidden = false;
        if (stripLabel) stripLabel.hidden = false;
        card.classList.remove('is-strip-on');
      };

      const pick = (btn, inSheet) => {
        const size = btn.dataset.size;
        const v = variantFor(size);
        if (!v) return;
        if (!v.available || btn.classList.contains('is-soldout')) {
          if (inSheet) {
            if (sheetSizes) sheetSizes.hidden = true;
            if (sheetNote) sheetNote.hidden = true;
            bindNotify(sheetNotify, size);
          } else {
            if (stripSizes) stripSizes.hidden = true;
            if (stripLabel) stripLabel.hidden = true;
            card.classList.add('is-strip-on');
            bindNotify(stripNotify, size);
          }
          return;
        }
        add(v);
      };

      card.querySelectorAll('.card__strip .size-btn').forEach((b) => b.addEventListener('click', () => pick(b, false)));
      if (sheet) sheet.querySelectorAll('.size-btn').forEach((b) => b.addEventListener('click', () => pick(b, true)));

      card.addEventListener('mouseleave', () => {
        if (stripNotify && !stripNotify.hidden && !stripNotify.classList.contains('is-done')) {
          const emailEl = stripNotify.querySelector('input[type="email"]');
          if (emailEl && document.activeElement === emailEl) return;
        }
        window.setTimeout(resetStrip, 250);
      });

      const openSheet = () => {
        if (!sheet) return;
        if (sheetSizes) sheetSizes.hidden = false;
        if (sheetNote) sheetNote.hidden = false;
        if (sheetNotify) {
          sheetNotify.hidden = true;
          sheetNotify.classList.remove('is-done');
        }
        sheet.classList.add('is-open');
        sheet.setAttribute('aria-hidden', 'false');
        document.body.classList.add('overflow-hidden');
      };
      const closeSheet = () => {
        if (!sheet) return;
        sheet.classList.remove('is-open');
        sheet.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('overflow-hidden');
      };
      const plus = card.querySelector('[data-sheet-open]');
      if (plus) plus.addEventListener('click', openSheet);
      if (sheet) {
        sheet.querySelectorAll('[data-sheet-close]').forEach((el) => el.addEventListener('click', closeSheet));
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && sheet.classList.contains('is-open')) closeSheet();
        });
      }
    });
  }

  /* ---------------------------------------------------------- Product page */
  function initProduct() {
    document.querySelectorAll('[data-product]').forEach((root) => {
      let variants = [];
      try {
        variants = JSON.parse(root.querySelector('[data-product-variants]').textContent);
      } catch (e) {
        return;
      }
      const optionGroups = Array.from(root.querySelectorAll('[data-option-group]'));
      const form = root.querySelector('product-form form');
      const idInput = form && form.querySelector('input[name="id"]');
      const priceEl = root.querySelector('[data-product-price]');
      const submit = root.querySelector('[data-product-submit]');
      const submitText = submit && submit.querySelector('span');
      const notify = root.querySelector('.product__aside > [data-notify]');
      const sizeIndex = parseInt(root.dataset.sizeIndex, 10);
      const selected = optionGroups.map((g) => {
        const on = g.querySelector('[aria-checked="true"]');
        return on ? on.dataset.value : null;
      });

      const find = (sel) => variants.find((v) => v.options.every((o, i) => sel[i] == null || o === sel[i]));

      const paint = () => {
        // Sold-out styling for the size group, given the other selections.
        optionGroups.forEach((g, gi) => {
          g.querySelectorAll('[data-value]').forEach((btn) => {
            const trial = selected.slice();
            trial[gi] = btn.dataset.value;
            const match = variants.filter((v) => v.options.every((o, i) => i === gi ? o === trial[gi] : trial[i] == null || o === trial[i]));
            const anyAvail = match.some((v) => v.available);
            btn.classList.toggle('is-soldout', match.length > 0 && !anyAvail);
            btn.classList.toggle('is-unavailable', match.length === 0);
            const on = btn.dataset.value === selected[gi];
            btn.classList.toggle('is-selected', on);
            btn.setAttribute('aria-checked', on ? 'true' : 'false');
          });
          const label = g.querySelector('[data-option-current]');
          if (label) label.textContent = selected[gi] || '';
        });
        const v = find(selected);
        if (idInput) {
          idInput.value = v ? v.id : '';
          idInput.disabled = !v;
        }
        if (priceEl && v) priceEl.textContent = money(v.price);
        const soldOut = !v || !v.available;
        if (submit) {
          submit.hidden = soldOut;
          if (!soldOut && submitText) submitText.textContent = submit.dataset.label || strings.addToBag || 'Add to bag';
        }
        if (notify) {
          if (soldOut) {
            const sizeVal = !isNaN(sizeIndex) ? selected[sizeIndex] : v ? v.title : '';
            bindNotify(notify, sizeVal || '');
          } else {
            notify.hidden = true;
          }
        }
        if (v && window.history.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', v.id);
          window.history.replaceState({}, '', url.toString());
        }
        if (v && v.featured_media) {
          const shot = root.querySelector(`[data-media-id="${v.featured_media}"]`);
          if (shot && !mobileMq.matches) {
            const rect = shot.getBoundingClientRect();
            if (rect.top < 0 || rect.top > window.innerHeight * 0.8) shot.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
          }
        }
        root.querySelectorAll('[data-set-add]').forEach((btn) => {
          const sz = !isNaN(sizeIndex) ? selected[sizeIndex] : null;
          if (sz) btn.dataset.size = sz;
        });
      };

      optionGroups.forEach((g, gi) => {
        g.querySelectorAll('[data-value]').forEach((btn) => {
          btn.addEventListener('click', () => {
            selected[gi] = btn.dataset.value;
            paint();
          });
        });
      });
      paint();

      // Added state on the main button
      if (form && submitText) {
        form.addEventListener('submit', () => {
          window.setTimeout(() => {
            submitText.textContent = strings.added || 'Added';
            submit.classList.add('pill--wine');
            window.setTimeout(() => {
              submitText.textContent = submit.dataset.label || strings.addToBag || 'Add to bag';
              submit.classList.remove('pill--wine');
            }, 2000);
          }, 400);
        });
      }

      // Size guide unit toggle
      const table = root.querySelector('[data-size-table]');
      if (table) {
        const cells = table.querySelectorAll('[data-in]');
        root.querySelectorAll('[data-unit]').forEach((btn) => {
          btn.addEventListener('click', () => {
            const unit = btn.dataset.unit;
            root.querySelectorAll('[data-unit]').forEach((b) => b.classList.toggle('is-on', b === btn));
            cells.forEach((c) => {
              const inches = parseFloat(c.dataset.in);
              c.textContent = unit === 'cm' ? Math.round(inches * 2.54) : inches;
            });
          });
        });
      }

      // Mobile gallery progress line
      const shots = root.querySelector('.product__shots');
      const bar = root.querySelector('.product__progress-bar');
      if (shots && bar) {
        const n = shots.children.length || 1;
        bar.style.width = `${(100 / n).toFixed(2)}%`;
        shots.addEventListener(
          'scroll',
          () => {
            const p = shots.scrollLeft / Math.max(1, shots.scrollWidth - shots.clientWidth);
            bar.style.left = `${(p * (1 - 1 / n) * 100).toFixed(2)}%`;
          },
          { passive: true }
        );
      }

      // Complete the set: add the piece in the currently selected size when possible
      root.querySelectorAll('[data-set-add]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const holder = btn.closest('[data-set-row]');
          const f = holder && holder.querySelector('product-form form');
          const input = f && f.querySelector('input[name="id"]');
          let list = [];
          try {
            list = JSON.parse(holder.querySelector('[data-set-variants]').textContent);
          } catch (e) {
            list = [];
          }
          const sz = btn.dataset.size;
          const match = list.filter((v) => (v.size || '').toLowerCase() === (sz || '').toLowerCase());
          const v = match.find((x) => x.available) || list.find((x) => x.available) || list[0];
          if (!v || !input) return;
          input.value = v.id;
          input.disabled = false;
          btn.textContent = strings.added || 'Added';
          if (f.requestSubmit) f.requestSubmit();
          else f.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        });
      });
    });
  }

  /* ------------------------------------------------------------ Set bundle */
  function initSetBundle() {
    document.querySelectorAll('[data-set-bundle]').forEach((root) => {
      let variants = [];
      try {
        variants = JSON.parse(root.querySelector('[data-bundle-variants]').textContent);
      } catch (e) {
        variants = [];
      }
      const pills = root.querySelectorAll('.set__sizes .size-pill');
      const form = root.querySelector('product-form form');
      const input = form && form.querySelector('input[name="id"]');
      const btn = root.querySelector('[data-bundle-submit] span');
      let size = (root.querySelector('.size-pill.is-selected') || {}).dataset ? root.querySelector('.size-pill.is-selected').dataset.size : null;
      const apply = () => {
        pills.forEach((p) => {
          const on = p.dataset.size === size;
          p.classList.toggle('is-selected', on);
          p.setAttribute('aria-checked', on ? 'true' : 'false');
        });
        const match = variants.filter((v) => !size || (v.size || '').toLowerCase() === size.toLowerCase());
        const v = match.find((x) => x.available) || match[0] || variants[0];
        if (input) {
          input.value = v ? v.id : '';
          input.disabled = !v;
        }
      };
      pills.forEach((p) =>
        p.addEventListener('click', () => {
          size = p.dataset.size;
          apply();
        })
      );
      apply();
      if (form && btn) {
        form.addEventListener('submit', () => {
          window.setTimeout(() => {
            btn.textContent = strings.added || 'Added';
            window.setTimeout(() => {
              btn.textContent = btn.dataset.label || 'Take the set';
            }, 2000);
          }, 400);
        });
      }
    });
  }

  /* --------------------------------------------------------- Email forms */
  function initEmailForms() {
    document.querySelectorAll('[data-email-form]').forEach((form) => {
      const btn = form.querySelector('[data-email-submit]');
      if (!btn) return;
      form.addEventListener('submit', () => {
        window.setTimeout(() => btn.setAttribute('aria-disabled', 'true'), 0);
      });
    });
  }

  /* ------------------------------------------------------- Auto-submit */
  function initAutoSubmit() {
    document.querySelectorAll('[data-auto-submit]').forEach((el) => {
      el.addEventListener('change', () => {
        if (el.form) el.form.submit();
      });
    });
  }

  /* -------------------------------------------------------------- Boot */
  function boot() {
    initTransitions();
    initHeader();
    initHero();
    reveal.init();
    initParallax();
    initFooter();
    initCards();
    initProduct();
    initSetBundle();
    initEmailForms();
    initAutoSubmit();

    if (designMode) {
      document.addEventListener('shopify:section:load', (e) => {
        initCards(e.target);
        reveal.init(e.target);
      });
    }

    // Cards rendered later (search suggestions, section rendering) get bound too.
    const mo = new MutationObserver((muts) => {
      let touch = false;
      muts.forEach((m) => {
        if (m.addedNodes.length) touch = true;
      });
      if (touch) {
        initCards();
        reveal.init();
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.Solaine = { reveal, bindNotify, initCards };
})();
